import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { debugError } from '../../utils/debug';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const adminEmail = (process.env.REACT_APP_ADMIN_EMAIL || '').trim().toLowerCase();
  const normalizedEmail = user?.email ? user.email.trim().toLowerCase() : '';
  const isAdmin = !!adminEmail && normalizedEmail === adminEmail;
  const ticketsPath = isAdmin ? '/admin/cases' : '/upload';
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = async () => {
    try {
      setProfileOpen(false);
      await logout();
      navigate('/');
    } catch (error) {
      debugError('Logout failed:', error);
    }
  };

  return (
    <header className="relative z-[70] border-b border-slate-200 bg-[#FDFBF7]/90 backdrop-blur">
      {profileOpen ? (
        <button
          type="button"
          aria-label="Close profile menu"
          onClick={() => setProfileOpen(false)}
          className="fixed inset-0 z-[60] cursor-default bg-transparent"
        />
      ) : null}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2 text-slate-900">
              <span className="text-2xl">⚖️</span>
              <span className="font-display font-bold text-xl">{t('appName')}</span>
            </Link>
          </div>

          <nav className="hidden md:flex items-center space-x-8">
            <LanguageSwitcher />
            {user ? (
              <>
                <div className="relative z-[80]">
                  <button
                    type="button"
                    onClick={() => setProfileOpen((prev) => !prev)}
                    className="flex items-center space-x-2 text-slate-600 hover:text-[#C6A87C] transition-colors duration-200"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#C6A87C] text-white flex items-center justify-center">
                      {user.photoURL ? (
                        <img 
                          src={user.photoURL} 
                          alt="Profile" 
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <span className="text-sm font-medium">
                          {user.displayName?.[0] || user.email?.[0] || 'U'}
                        </span>
                      )}
                    </div>
                    <span className="hidden lg:block">
                      {user.displayName || user.email}
                    </span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  <div
                    className={`absolute right-0 top-full pt-2 z-[80] transition-all duration-200 ${
                      profileOpen ? 'opacity-100 visible pointer-events-auto' : 'opacity-0 invisible pointer-events-none'
                    }`}
                  >
                    <div className="w-48 bg-white rounded-md shadow-lg py-1">
                      <Link
                        to={ticketsPath}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setProfileOpen(false)}
                      >
                        {isAdmin ? 'Admin dashboard' : t('nav.profile')}
                      </Link>
                      <div className="border-t border-gray-100"></div>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('nav.signOut')}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-sm font-medium text-slate-600 hover:text-[#C6A87C] transition-colors duration-200"
                >
                  {t('nav.signIn')}
                </Link>
                <Link
                  to="/signup"
                  className="rounded-lg bg-[#C6A87C] px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-[#B09265]"
                >
                  {t('nav.getStarted')}
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              className="text-slate-600 hover:text-[#C6A87C] focus:outline-none focus:text-[#C6A87C]"
              aria-label="Open menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu - you can add state management for this */}
      <div className="md:hidden hidden">
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-[#FDFBF7] border-t border-slate-200">
          {user ? (
            <>
              <Link
                to={ticketsPath}
                className="block px-3 py-2 text-slate-600 hover:text-[#C6A87C] transition-colors duration-200"
              >
                {isAdmin ? 'Admin dashboard' : t('nav.profile')}
              </Link>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-3 py-2 text-slate-600 hover:text-[#C6A87C] transition-colors duration-200"
              >
                {t('nav.signOut')}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="block px-3 py-2 text-slate-600 hover:text-[#C6A87C] transition-colors duration-200"
              >
                {t('nav.signIn')}
              </Link>
              <Link
                to="/signup"
                className="block px-3 py-2 text-slate-600 hover:text-[#C6A87C] transition-colors duration-200"
              >
                {t('nav.getStarted')}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
